import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, BookMarked, CalendarPlus, RefreshCw, Search, Trash2, Upload, Plus } from 'lucide-react';
import { formatDistanceToNowStrict } from 'date-fns';
import { scheduledCommandsApi } from '../../services/scheduledCommandsApi';
import type { TellrawLibraryEntry } from '../../types/scheduledCommands';
import { FAMILY_LABELS, compileTellraw } from '../../utils/tellraw';
import type { VersionFamily } from '../../utils/tellraw/types';
import { EmptyState } from '../ui/EmptyState';
import { SkeletonLoader } from '../SkeletonLoader';
import { ConfirmationModal } from '../ConfirmationModal';
import { TellrawPreview } from './tellraw/TellrawPreview';

const FAMILIES: VersionFamily[] = ['legacy', 'modern', 'current'];

const SOURCE_LABEL: Record<TellrawLibraryEntry['source'], string> = {
  assistant: 'Written with the assistant',
  import: 'Imported',
  manual: 'Written by hand',
};

interface TellrawLibraryPanelProps {
  canWrite: boolean;
}

/**
 * Saved tellraws, kept so a message that works once can be reused without
 * writing it again. Each card renders the message the way players will see it,
 * from the saved run model, so a card is also a check that it still compiles.
 */
export const TellrawLibraryPanel = ({ canWrite }: TellrawLibraryPanelProps) => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState<TellrawLibraryEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [deleting, setDeleting] = useState<TellrawLibraryEntry | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      setEntries(await scheduledCommandsApi.library());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The library could not be loaded.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const previews = useMemo(
    () => new Map((entries ?? []).map((entry) => [entry.id, compileTellraw(entry.message, entry.compat).component])),
    [entries],
  );

  const shown = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return entries ?? [];
    return (entries ?? []).filter(
      (e) => e.name.toLowerCase().includes(query) || (e.notes ?? '').toLowerCase().includes(query) || e.command.toLowerCase().includes(query),
    );
  }, [entries, search]);

  const remove = async (entry: TellrawLibraryEntry) => {
    setBusy(true);
    try {
      await scheduledCommandsApi.deleteLibraryEntry(entry.id);
      setEntries((prev) => (prev ?? []).filter((e) => e.id !== entry.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The entry could not be deleted.');
    } finally {
      setBusy(false);
      setDeleting(null);
    }
  };

  if (error && !entries) {
    return (
      <EmptyState
        tone="danger"
        icon={AlertCircle}
        title="The tellraw library could not be loaded"
        description={error}
        action={
          <button type="button" onClick={() => void load()} className="glass-button">
            <RefreshCw className="w-4 h-4" />
            Retry
          </button>
        }
      />
    );
  }

  if (!entries) {
    return (
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {Array.from({ length: 6 }).map((_, i) => <SkeletonLoader key={i} className="h-44" />)}
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <EmptyState
        icon={BookMarked}
        title="No saved tellraws yet"
        description="Messages written with the assistant or imported from an existing command land here, ready to reuse in any schedule."
        action={
          canWrite && (
            <div className="flex flex-wrap justify-center gap-2">
              <button type="button" onClick={() => navigate('/scheduled-commands/library/new?import=1')} className="glass-button">
                <Upload className="w-4 h-4" />
                Import a tellraw
              </button>
              <button type="button" onClick={() => navigate('/scheduled-commands/library/new')} className="btn-primary">
                <Plus className="w-4 h-4" />
                New tellraw
              </button>
            </div>
          )
        }
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row md:items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400" />
          <input
            className="glass-input w-full pl-9"
            placeholder="Search saved tellraws…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search saved tellraws"
          />
        </div>
        <span className="text-xs text-glass-400 tabular-nums md:ml-auto">{shown.length} of {entries.length}</span>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {shown.length === 0 ? (
        <EmptyState icon={Search} title="Nothing matches that search" description="Try a shorter word, or part of the message text." />
      ) : (
        <ul className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {shown.map((entry) => (
            <li key={entry.id}>
              <div
                role="link"
                tabIndex={0}
                onClick={() => navigate(`/scheduled-commands/library/${encodeURIComponent(entry.id)}`)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') navigate(`/scheduled-commands/library/${encodeURIComponent(entry.id)}`);
                }}
                className="glass-card glass-card-interactive p-4 h-full flex flex-col gap-3 cursor-pointer"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-white truncate">{entry.name}</p>
                  {entry.notes && <p className="text-xs text-glass-400 mt-0.5 line-clamp-2">{entry.notes}</p>}
                </div>
                <TellrawPreview component={previews.get(entry.id)} className="max-h-32 overflow-hidden" />
                <div className="flex flex-wrap gap-1">
                  {FAMILIES.filter((f) => entry.compat[f]).map((f) => (
                    <span key={f} className="badge badge-neutral tabular-nums">{FAMILY_LABELS[f]}</span>
                  ))}
                </div>
                <div className="mt-auto flex items-center gap-2 text-xs text-glass-500">
                  <span className="flex-1 min-w-0 truncate" title={SOURCE_LABEL[entry.source]}>
                    {entry.updated_by_name ?? entry.created_by_name ?? 'Someone'} ·{' '}
                    {formatDistanceToNowStrict(new Date(entry.updated_at), { addSuffix: true })}
                  </span>
                  {canWrite && (
                    <>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/scheduled-commands/new?library=${encodeURIComponent(entry.id)}`);
                        }}
                        className="btn-ghost btn-xs"
                        title="Start a new schedule with this message"
                      >
                        <CalendarPlus className="w-3.5 h-3.5" />
                        Schedule it
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleting(entry);
                        }}
                        className="btn-icon btn-icon-sm text-red-400 hover:text-red-300"
                        title="Delete from the library"
                        aria-label={`Delete ${entry.name} from the library`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      <ConfirmationModal
        isOpen={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting && void remove(deleting)}
        title={deleting ? `Delete ${deleting.name}?` : 'Delete?'}
        message="It leaves the library. Schedules that already use this message keep their own copy and carry on."
        confirmText="Delete"
        isLoading={busy}
      />
    </div>
  );
};
